// src/app/page.tsx

"use client";

import { Spin } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Brain,
  Check,
  Clock,
  FileText,
  GraduationCap,
  Layers,
  Presentation,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import type React from "react";
import { ReactNode, useCallback, useEffect } from "react";
import useSWR from "swr";
import ErrorBoundary from "@/components/ErrorBoundary";
import { trackSignUp } from "@/utils/analytics";
import { safeStringify } from "@/utils/api";

const SampleProcessedDocumentResultsViewer = dynamic(
  () => import("@/components/SampleProcessedDocumentResultsViewer"),
  { ssr: false },
);
const Features = dynamic(() => import("@/components/Features"), { ssr: false });
const TechCapabilities = dynamic(() => import("@/components/TechCapabilities"), { ssr: false });
const HighlightReel = dynamic(() => import("@/components/HighlightReel"), { ssr: false });
const GlassNavbar = dynamic(() => import("@/components/GlassNavbar"), { ssr: false });
const HeroMeshBackground = dynamic(() => import("@/components/HeroMeshBackground"), { ssr: false });

// Premium Feature Card Component
interface FeatureCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  stats?: { value: string; label: string };
  delay?: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  iconBg,
  title,
  description,
  stats,
  delay = 0,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className="group relative"
  >
    <div
      className="relative bg-white rounded-3xl p-8 border border-gray-100 shadow-lg
      hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden h-full"
    >
      {/* Gradient glow effect */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-purple-500/5
        opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      />

      {/* Animated corner accent */}
      <div
        className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand-500/10 to-transparent
        rounded-bl-[100px] translate-x-8 -translate-y-8 group-hover:translate-x-4 group-hover:-translate-y-4
        transition-transform duration-500"
      />

      {/* Icon */}
      <motion.div
        className={`relative w-14 h-14 rounded-2xl ${iconBg} flex items-center justify-center mb-6
          shadow-lg transform-gpu`}
        whileHover={{ scale: 1.1, rotate: 5 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        {icon}
      </motion.div>

      {/* Content */}
      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors">
        {title}
      </h3>
      <p className="text-gray-600 leading-relaxed mb-4">{description}</p>

      {/* Stats badge */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + 0.3 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100
            rounded-full border border-gray-200"
        >
          <span className="text-xl font-bold text-gradient">{stats.value}</span>
          <span className="text-sm text-gray-500">{stats.label}</span>
        </motion.div>
      )}
    </div>
  </motion.div>
);

const sampleFiles = {
  markdown: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/optimized_document.md",
    displayName: "Optimized Formatted Document (Markdown)",
  },
  gradedMultipleChoiceQuizHtml: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/graded_multiple_choice_quiz.html",
    displayName: "Graded Multiple Choice Quiz (as HTML)",
  },
  presentationSlidesPdf: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/presentation_slides.pdf",
    displayName: "Presentation Slides PDF",
  },
  presentationSlidesHtml: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/presentation_slides.html",
    displayName: "Presentation Slides HTML",
  },
  readabilityAnalysis: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/readability_heatmap.html",
    displayName: "Readability Analysis (HTML)",
  },
  mindmapOutline: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/mindmap_outline.html",
    displayName: "Mindmap Outline (HTML)",
  },
  mindmapDiagram: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/mindmap.html",
    displayName: "Mindmap Diagram (HTML)",
  },
  lessonPlan: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/lesson_plan.md",
    displayName: "Lesson Plan (Markdown)",
  },
  gradeLevelVersion: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/grade_level_version_elementary.md",
    displayName: "Grade Level Version - Elementary (Markdown)",
  },
  flashCards: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/flash_cards.csv",
    displayName: "Anki Flash Cards (CSV)",
  },
  flashCardsApkg: {
    path: "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d/anki_flashcards.apkg",
    displayName: "Anki Flash Cards (Ready for Import)",
  },
};

// Fetchers for SWR
const fetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to fetch");
  }
  return res.json();
};

const textFetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("Failed to fetch file: " + url);
  }
  return res.text();
};

const binaryFetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("Failed to fetch file: " + url);
  }
  const blob = await res.blob();
  return blob;
};

// Loading screen component for authenticated users
const AuthLoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-lg">
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <Spin size="large" />
      <p className="mt-4 text-gray-600 font-medium">Taking you to your dashboard...</p>
    </div>
  </div>
);

export default function Landing() {
  const { data: session, status } = useSession();
  const router = useRouter();

  const shouldCheckFreeCredits = status === "authenticated" && session?.user?.email;
  const { data: freeCreditsData, error: freeCreditsError } = useSWR(
    shouldCheckFreeCredits ? "/api/check-free-credits" : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    },
  );

  const { data: fileContentsData } = useSWR(
    "sample-files",
    async () => {
      const entries = await Promise.all(
        Object.entries(sampleFiles).map(async ([key, value]) => {
          if (value.path.endsWith(".apkg")) {
            const blob = await binaryFetcher(value.path);
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            return [key, uint8Array];
          } else {
            const text = await textFetcher(value.path);
            return [key, text];
          }
        }),
      );
      return Object.fromEntries(entries);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (status === "authenticated" && session?.user?.email) {
      try {
        if (freeCreditsData || freeCreditsError) {
          trackSignUp(session.user.email);
          router.push("/dashboard");
        }
      } catch (error) {
        console.error("Error in authentication flow:", safeStringify(error));
        router.push("/dashboard");
      }
    }
  }, [status, session, router, freeCreditsData, freeCreditsError]);

  const handleDownloadZip = useCallback(() => {
    const link = document.createElement("a");
    link.href =
      "/sample_files/Combined_Document_Optimizer_Output_File_Bundle__Document__buffett_1261db1d.zip";
    link.download = "Sample_Document_Processing_Output.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Show loading screen for authenticated users (prevents flash of landing page)
  if (status === "loading" || status === "authenticated") {
    return <AuthLoadingScreen />;
  }

  const fileContents = fileContentsData || {};

  return (
    <ErrorBoundary
      componentName="LandingPage"
      fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Something went wrong.</h1>
          <p className="text-lg text-gray-700 mb-2">An unexpected error has occurred.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-3 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      }
    >
      <div className="flex flex-col min-h-screen bg-white">
        {/* Premium Glassmorphism Navbar */}
        <GlassNavbar showCTA={true} ctaText="Get started free" />

        {/* HERO SECTION - Premium design with animated mesh background */}
        <section className="relative pt-28 pb-20 sm:pt-36 sm:pb-32 overflow-hidden">
          {/* Premium animated mesh background */}
          <HeroMeshBackground variant="default" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Content with staggered animations */}
            <motion.div
              className="text-center max-w-4xl mx-auto"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.12,
                    delayChildren: 0.1,
                  },
                },
              }}
            >
              {/* Badge */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20, filter: "blur(10px)" },
                  visible: {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    transition: { duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] },
                  },
                }}
              >
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm
                  border border-brand-200/50 shadow-sm shadow-brand-500/5 text-brand-700 text-sm font-medium mb-8"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500" />
                  </span>
                  AI-Powered Document Processing
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-navy-900 tracking-tight leading-[1.08]"
                variants={{
                  hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
                  visible: {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] },
                  },
                }}
              >
                Transform Any Document Into{" "}
                <span className="text-gradient-animated relative">
                  Learning Materials
                  {/* Decorative underline */}
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-3 text-brand-400/40"
                    viewBox="0 0 300 12"
                    fill="none"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2 8.5C50 2 100 2 150 6C200 10 250 10 298 4"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                className="mt-8 text-lg sm:text-xl lg:text-2xl text-navy-600/80 max-w-2xl mx-auto leading-relaxed font-medium"
                variants={{
                  hidden: { opacity: 0, y: 25, filter: "blur(8px)" },
                  visible: {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    transition: { duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] },
                  },
                }}
              >
                Upload PDFs, Word docs, or scanned files. Get quizzes, flashcards, summaries,
                presentations, and more — all powered by advanced AI.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] },
                  },
                }}
              >
                <motion.button
                  onClick={() => signIn("google")}
                  className="btn btn-primary btn-lg btn-shine group relative overflow-hidden"
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Get started free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
                <motion.div
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Link
                    href="/pricing"
                    className="btn btn-secondary btn-lg backdrop-blur-sm bg-white/80 hover:bg-white"
                  >
                    View pricing
                  </Link>
                </motion.div>
              </motion.div>

              {/* Trust indicators with staggered entrance */}
              <motion.div
                className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.08,
                      delayChildren: 0.1,
                    },
                  },
                }}
              >
                {[
                  {
                    icon: Zap,
                    label: "50 free credits",
                    bg: "bg-emerald-50/80",
                    iconColor: "text-emerald-600",
                    textColor: "text-emerald-700",
                  },
                  {
                    icon: Shield,
                    label: "No credit card required",
                    bg: "bg-blue-50/80",
                    iconColor: "text-blue-600",
                    textColor: "text-blue-700",
                  },
                  {
                    icon: Clock,
                    label: "Instant results",
                    bg: "bg-purple-50/80",
                    iconColor: "text-purple-600",
                    textColor: "text-purple-700",
                  },
                ].map(({ icon: Icon, label, bg, iconColor, textColor }, index) => (
                  <motion.span
                    key={label}
                    className={`flex items-center gap-2 px-4 py-2 ${bg} backdrop-blur-sm rounded-full
                      border border-white/50 shadow-sm cursor-default`}
                    variants={{
                      hidden: { opacity: 0, scale: 0.8, y: 10 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        transition: { type: "spring", stiffness: 400, damping: 20 },
                      },
                    }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                    <span className={`text-sm font-medium ${textColor}`}>{label}</span>
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Highlight Reel */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold text-gray-900">See the Transformation</h2>
              <p className="mt-3 text-lg text-gray-600">
                From raw documents to polished learning materials
              </p>
            </motion.div>
            <HighlightReel />
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Everything You Need</h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                Powerful AI tools to transform your documents into comprehensive learning materials
              </p>
            </motion.div>
            <Features />
          </div>
        </section>

        {/* Tech Capabilities */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TechCapabilities />
          </div>
        </section>

        {/* Sample Output */}
        <section className="py-20 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 mb-6"
              >
                <Check className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Live Demo</span>
              </motion.div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Try It Yourself</h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                Explore a real example of our document processing output — no signup required
              </p>
            </motion.div>
            <SampleProcessedDocumentResultsViewer
              fileContents={fileContents}
              sampleFiles={sampleFiles}
              onDownloadZip={handleDownloadZip}
            />
          </div>
        </section>

        {/* Premium Features Showcase */}
        <section className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gradient-to-br from-brand-100/30 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-gradient-to-br from-purple-100/30 to-transparent rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div
              className="text-center mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 border border-brand-100 mb-6"
              >
                <Sparkles className="w-4 h-4 text-brand-600" />
                <span className="text-sm font-medium text-brand-700">
                  Intelligent Document Processing
                </span>
              </motion.div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
                Eight Output Formats, <span className="text-gradient">One Upload</span>
              </h2>
              <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Advanced AI analyzes your documents and generates comprehensive learning materials
                tailored to your needs — all from a single file upload.
              </p>
            </motion.div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              <FeatureCard
                icon={<Brain className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-purple-500 to-purple-600"
                title="Smart Quizzes"
                description="Auto-generated multiple choice and short answer questions that test true comprehension."
                stats={{ value: "2", label: "quiz types" }}
                delay={0}
              />
              <FeatureCard
                icon={<Layers className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-blue-500 to-blue-600"
                title="Anki Flashcards"
                description="Export spaced repetition flashcards ready for import. Optimized for long-term memory."
                stats={{ value: ".apkg", label: "export" }}
                delay={0.1}
              />
              <FeatureCard
                icon={<Presentation className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-orange-500 to-orange-600"
                title="Slide Decks"
                description="Beautiful presentation slides with key concepts, visuals, and structured talking points."
                stats={{ value: "PDF", label: "& HTML" }}
                delay={0.2}
              />
              <FeatureCard
                icon={<FileText className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-brand-500 to-brand-600"
                title="Summaries"
                description="Comprehensive overviews that capture essential information without losing context."
                delay={0.3}
              />
              <FeatureCard
                icon={<BarChart3 className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-emerald-500 to-emerald-600"
                title="Mind Maps"
                description="Interactive concept maps showing relationships between ideas for visual learners."
                delay={0.4}
              />
              <FeatureCard
                icon={<BookOpen className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-rose-500 to-rose-600"
                title="Readability Analysis"
                description="Grade-level metrics and complexity analysis with sentence-by-sentence heatmaps."
                delay={0.5}
              />
              <FeatureCard
                icon={<GraduationCap className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-indigo-500 to-indigo-600"
                title="Lesson Plans"
                description="Structured teaching guides with objectives, activities, and assessment strategies."
                delay={0.6}
              />
              <FeatureCard
                icon={<Zap className="w-7 h-7 text-white" />}
                iconBg="bg-gradient-to-br from-amber-500 to-amber-600"
                title="Instant Processing"
                description="Advanced AI processing delivers all outputs in minutes, not hours."
                stats={{ value: "<5", label: "minutes" }}
                delay={0.7}
              />
            </div>

            {/* Bottom CTA */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <motion.button
                onClick={() => signIn("google")}
                className="btn btn-primary btn-lg btn-shine group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start transforming documents
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <p className="mt-4 text-sm text-gray-500">
                50 free credits included • No credit card required
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-brand-50/30 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-100/40 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-100/40 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Ready to Transform Your Documents?
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Start with 50 free credits. No credit card required.
              </p>
            </motion.div>
            <motion.div
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.button
                onClick={() => signIn("google")}
                className="btn btn-primary btn-lg btn-shine group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get started free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link href="/pricing" className="btn btn-secondary btn-lg">
                  View pricing
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative bg-gray-900 overflow-hidden">
          {/* Gradient accent at top */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/50 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
              {/* Brand */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center shadow-lg shadow-brand-500/25">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">FixMyDocuments</span>
                </div>
                <p className="text-gray-400 max-w-sm leading-relaxed">
                  Transform any document into comprehensive learning materials with advanced AI.
                  Quizzes, flashcards, summaries, and more.
                </p>
              </div>

              {/* Links */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                  Product
                </h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      href="/pricing"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => signIn("google")}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Get Started
                    </button>
                  </li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
                  Legal
                </h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      href="/privacy-policy"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/terms-of-service"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} FixMyDocuments. All rights reserved.
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>All systems operational</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
}
